import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { getInitials } from "../../../requests/utilities/helpers";
import { formatTimestamp } from "../../../../../helpers/helpers";
import AutosizingTextarea from "../../AutosizingTextarea";

// The one entry model, rendered the same way everywhere: avatar, text,
// author, time. These are entries on non-category blocks (metric cards,
// tables, agents, weather); category blocks keep CategoryEntry, which shares
// this exact visual structure plus typed values and photos.

export function BlockEntry(props) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(props.entry.value_text);

  const isAuthor =
    props.currentUser &&
    props.entry.store_team_member?.user_id === props.currentUser.user_id;
  const canDelete = isAuthor || props.currentUser?.is_admin;

  if (isEditing) {
    return (
      <div className="w-full">
        <AutosizingTextarea
          className="w-full text-sm text-slate-800 border border-slate-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-violet-500 resize-none"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
        />
        <div className="flex items-center gap-2 mt-1.5">
          <button
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              editValue.trim()
                ? "bg-violet-700 text-white cursor-pointer hover:bg-violet-800"
                : "bg-slate-100 text-slate-400"
            }`}
            disabled={!editValue.trim()}
            onClick={() => {
              props.post_logbook_block_entry(
                props.entry.block_uuid,
                editValue.trim(),
                props.entry.is_response,
                props.entry.uuid
              );
              setIsEditing(false);
            }}
          >
            Save
          </button>
          <button
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-200 hover:bg-slate-50"
            onClick={() => {
              setEditValue(props.entry.value_text);
              setIsEditing(false);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-violet-100 text-xs font-semibold text-violet-700 flex items-center justify-center mt-0.5 shrink-0">
        <p>{getInitials(props.entry.store_team_member.full_name)}</p>
      </div>
      <div className="flex-1">
        <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
          {props.entry.value_text}
        </p>
        <p className="text-xs text-slate-400 mt-1.5">
          {props.entry.is_response && (
            <span className="inline-block text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 mr-1.5 align-[1px]">
              Required response
            </span>
          )}
          {props.entry.store_team_member.full_name} &middot;{" "}
          {formatTimestamp(props.entry.created_at)}
        </p>
      </div>
      {(isAuthor || canDelete) && (
        <div className="flex items-center gap-1.5 shrink-0">
          {isAuthor && (
            <div
              className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors"
              onClick={() => setIsEditing(true)}
            >
              <EditOutlinedIcon style={{ fontSize: "16px", color: "#64748B" }} />
            </div>
          )}
          {canDelete && (
            <div
              className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors"
              onClick={() => props.delete_logbook_block_entry(props.entry.uuid)}
            >
              <DeleteOutlinedIcon style={{ fontSize: "16px", color: "#64748B" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function BlockEntryComposer(props) {
  const [value, setValue] = React.useState("");

  return (
    <div className="w-full">
      <AutosizingTextarea
        autoFocus
        className="w-full text-sm text-slate-800 border border-slate-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-violet-500 resize-none"
        placeholder={
          props.isResponse
            ? "Explain the flagged metric..."
            : "Add an entry..."
        }
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className="flex items-center gap-2 mt-1.5">
        <button
          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            value.trim()
              ? "bg-violet-700 text-white cursor-pointer hover:bg-violet-800"
              : "bg-slate-100 text-slate-400"
          }`}
          disabled={!value.trim()}
          onClick={() => {
            props.post_logbook_block_entry(
              props.blockUuid,
              value.trim(),
              Boolean(props.isResponse),
              null
            );
            setValue("");
            props.onClose();
          }}
        >
          {props.isResponse ? "Save response" : "Save entry"}
        </button>
        <button
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-200 hover:bg-slate-50"
          onClick={props.onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Entries + the required-response strip + the quiet "Add entry"
// affordance, shared by every non-category block. The affordance is always
// visible (not hover-revealed) so mousing around never reflows the canvas.
export function BlockEntrySection(props) {
  const [showComposer, setShowComposer] = React.useState(false);
  const [composerIsResponse, setComposerIsResponse] = React.useState(false);

  const entries = props.entries || [];
  const showResponseStrip = props.responseOutstanding && !showComposer;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {entries.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100 flex flex-col gap-2.5">
          {entries.map((entry) => (
            <BlockEntry
              key={entry.uuid}
              entry={entry}
              currentUser={props.currentUser}
              post_logbook_block_entry={props.post_logbook_block_entry}
              delete_logbook_block_entry={props.delete_logbook_block_entry}
            />
          ))}
        </div>
      )}

      {showResponseStrip && (
        <div
          className="mt-2 flex items-center gap-1.5 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-rose-100 transition-colors"
          onClick={() => {
            setComposerIsResponse(true);
            setShowComposer(true);
          }}
        >
          <p className="text-xs font-semibold text-rose-700">
            ⚠ Response required &middot;{" "}
            <span className="underline">Add entry</span>
          </p>
        </div>
      )}

      {showComposer ? (
        <div className="mt-2">
          <BlockEntryComposer
            blockUuid={props.blockUuid}
            isResponse={composerIsResponse}
            post_logbook_block_entry={props.post_logbook_block_entry}
            onClose={() => {
              setShowComposer(false);
              setComposerIsResponse(false);
            }}
          />
        </div>
      ) : (
        !showResponseStrip && (
          <div
            className="mt-2 flex items-center gap-1 cursor-pointer text-slate-400 hover:text-violet-700"
            onClick={() => {
              setComposerIsResponse(false);
              setShowComposer(true);
            }}
          >
            <AddIcon style={{ fontSize: "14px" }} />
            <p className="text-xs font-medium">Add entry</p>
          </div>
        )
      )}
    </div>
  );
}
